import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { NODE_TYPES, NODE_STATUSES } from './narrativeConstants';
import {
  Loader2, Plus, X, BookOpen, Link2, Settings2, Feather, Lock, Star, Tag,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const EMPTY_NODE = {
  title: '',
  node_type: 'encounter',
  summary: '',
  description: '',
  triggers: '',
  consequences_text: '',
  mj_notes: '',
  status: 'not_started',
  act: '',
  arc: '',
  tags: [],
  linked_npcs: [],
  linked_locations: [],
  linked_quests: [],
  linked_items: [],
  is_secret: false,
  is_main_plot: true,
};

const TABS = [
  { key: 'general', icon: Feather, label: { fr: 'Général', en: 'General' } },
  { key: 'narrative', icon: BookOpen, label: { fr: 'Narratif', en: 'Narrative' } },
  { key: 'links', icon: Link2, label: { fr: 'Liens', en: 'Links' } },
  { key: 'meta', icon: Settings2, label: { fr: 'Meta', en: 'Meta' } },
];

export default function NarrativeNodeEditor({
  open, onOpenChange, node, language = 'fr', saving, onSave,
  npcs = [], locations = [], quests = [],
}) {
  const [form, setForm] = useState(EMPTY_NODE);
  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const lang = language;
  const isEdit = !!node?.id;

  useEffect(() => {
    if (node) {
      setForm({ ...EMPTY_NODE, ...node });
    } else {
      setForm(EMPTY_NODE);
    }
    setTagInput('');
    setActiveTab('general');
  }, [node, open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      handleChange('tags', [...form.tags, tag]);
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    handleChange('tags', form.tags.filter(t => t !== tag));
  };

  const toggleLinked = (field, id) => {
    const current = form[field] || [];
    if (current.includes(id)) {
      handleChange(field, current.filter(x => x !== id));
    } else {
      handleChange(field, [...current, id]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const { id, campaign_id, history, created_at, updated_at, ...editableFields } = form;
    onSave(editableFields);
  };

  const typeConfig = NODE_TYPES[form.node_type] || NODE_TYPES.encounter;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* ── Header with type color accent ── */}
        <div className="relative shrink-0">
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{ background: `linear-gradient(135deg, ${typeConfig.color}, transparent 70%)` }}
          />
          <DialogHeader className="relative px-5 pt-5 pb-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: typeConfig.color + '18', border: `1px solid ${typeConfig.color}25` }}
              >
                {React.createElement(typeConfig.icon, { className: 'h-5 w-5', style: { color: typeConfig.color } })}
              </div>
              <div>
                <DialogTitle className="font-cinzel text-base">
                  {isEdit
                    ? (lang === 'fr' ? 'Modifier le nœud' : 'Edit Node')
                    : (lang === 'fr' ? 'Nouveau nœud narratif' : 'New Narrative Node')
                  }
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  {isEdit
                    ? (lang === 'fr' ? 'Mettez à jour les informations de cette scène' : 'Update this scene\'s information')
                    : (lang === 'fr' ? 'Créez une nouvelle scène dans votre fil narratif' : 'Create a new scene in your narrative thread')
                  }
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* ── Tab navigation ── */}
          <div className="relative px-5 pb-0">
            <div className="flex gap-0.5 border-b border-border/50">
              {TABS.map(tab => {
                const TabIcon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-all relative',
                      isActive
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground/70',
                    )}
                  >
                    <TabIcon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{tab.label[lang]}</span>
                    {isActive && (
                      <span
                        className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                        style={{ background: typeConfig.color }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Form body ── */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <ScrollArea className="flex-1">
            <div className="px-5 py-4">

              {/* ━━ General tab ━━ */}
              {activeTab === 'general' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">{lang === 'fr' ? 'Titre' : 'Title'} *</Label>
                    <Input
                      value={form.title}
                      onChange={e => handleChange('title', e.target.value)}
                      placeholder={lang === 'fr' ? 'Ex: Rencontre avec le capitaine renégat' : 'Ex: Meeting the renegade captain'}
                      className="h-10"
                      required
                      autoFocus
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold">{lang === 'fr' ? 'Type de scène' : 'Scene Type'}</Label>
                      <Select value={form.node_type} onValueChange={v => handleChange('node_type', v)}>
                        <SelectTrigger className="h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(NODE_TYPES).map(([key, cfg]) => {
                            const NodeIcon = cfg.icon;
                            return (
                              <SelectItem key={key} value={key}>
                                <span className="flex items-center gap-2">
                                  <NodeIcon className="h-3.5 w-3.5" style={{ color: cfg.color }} />
                                  {cfg.label[lang]}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-semibold">{lang === 'fr' ? 'Statut' : 'Status'}</Label>
                      <Select value={form.status} onValueChange={v => handleChange('status', v)}>
                        <SelectTrigger className="h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(NODE_STATUSES).map(([key, cfg]) => (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.color }} />
                                {cfg.label[lang]}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">{lang === 'fr' ? 'Résumé rapide' : 'Quick Summary'}</Label>
                    <Input
                      value={form.summary}
                      onChange={e => handleChange('summary', e.target.value)}
                      placeholder={lang === 'fr' ? 'Résumé en une phrase...' : 'One-line summary...'}
                      className="h-10"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">{lang === 'fr' ? 'Description détaillée' : 'Detailed Description'}</Label>
                    <Textarea
                      value={form.description}
                      onChange={e => handleChange('description', e.target.value)}
                      rows={5}
                      placeholder={lang === 'fr' ? 'Description complète de la scène...' : 'Full scene description...'}
                      className="resize-none text-sm"
                    />
                  </div>
                </div>
              )}

              {/* ━━ Narrative tab ━━ */}
              {activeTab === 'narrative' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">{lang === 'fr' ? 'Conditions de déclenchement' : 'Trigger Conditions'}</Label>
                    <Textarea
                      value={form.triggers}
                      onChange={e => handleChange('triggers', e.target.value)}
                      rows={3}
                      placeholder={lang === 'fr' ? 'Quelles conditions doivent être remplies...' : 'What conditions must be met...'}
                      className="resize-none text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">{lang === 'fr' ? 'Conséquences possibles' : 'Possible Consequences'}</Label>
                    <Textarea
                      value={form.consequences_text}
                      onChange={e => handleChange('consequences_text', e.target.value)}
                      rows={3}
                      placeholder={lang === 'fr' ? 'Si tué → ... / Si épargné → ...' : 'If killed → ... / If spared → ...'}
                      className="resize-none text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Lock className="h-3 w-3 text-amber-500" />
                      <Label className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                        {lang === 'fr' ? 'Notes MJ (secrètes)' : 'GM Notes (secret)'}
                      </Label>
                    </div>
                    <Textarea
                      value={form.mj_notes}
                      onChange={e => handleChange('mj_notes', e.target.value)}
                      rows={3}
                      className="resize-none text-sm border-amber-500/20 focus-visible:ring-amber-500/30"
                      placeholder={lang === 'fr' ? 'Notes visibles uniquement par le MJ...' : 'Notes visible only to GM...'}
                    />
                  </div>
                </div>
              )}

              {/* ━━ Links tab ━━ */}
              {activeTab === 'links' && (
                <div className="space-y-5">
                  {/* NPCs */}
                  {npcs.length > 0 && (
                    <LinkedSection
                      label={lang === 'fr' ? 'PNJ impliqués' : 'Involved NPCs'}
                      items={npcs}
                      selected={form.linked_npcs}
                      onToggle={(id) => toggleLinked('linked_npcs', id)}
                      getLabel={npc => npc.name}
                      color="#ec4899"
                    />
                  )}

                  {/* Locations */}
                  {locations.length > 0 && (
                    <LinkedSection
                      label={lang === 'fr' ? 'Lieux liés' : 'Linked Locations'}
                      items={locations}
                      selected={form.linked_locations}
                      onToggle={(id) => toggleLinked('linked_locations', id)}
                      getLabel={loc => loc.name}
                      color="#10b981"
                    />
                  )}

                  {/* Quests */}
                  {quests.length > 0 && (
                    <LinkedSection
                      label={lang === 'fr' ? 'Quêtes liées' : 'Linked Quests'}
                      items={quests}
                      selected={form.linked_quests}
                      onToggle={(id) => toggleLinked('linked_quests', id)}
                      getLabel={q => q.title}
                      color="#8b5cf6"
                    />
                  )}

                  {/* Items (free text) */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">{lang === 'fr' ? 'Objets / Indices liés' : 'Linked Items / Clues'}</Label>
                    {form.linked_items.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {form.linked_items.map((item, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 px-2 py-0.5 rounded-lg">
                            {item}
                            <X
                              className="h-3 w-3 cursor-pointer opacity-60 hover:opacity-100"
                              onClick={() => handleChange('linked_items', form.linked_items.filter((_, idx) => idx !== i))}
                            />
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        placeholder={lang === 'fr' ? 'Nom de l\'objet...' : 'Item name...'}
                        className="h-9"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = e.target.value.trim();
                            if (v) {
                              handleChange('linked_items', [...form.linked_items, v]);
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {lang === 'fr' ? 'Appuyez sur Entrée pour ajouter' : 'Press Enter to add'}
                    </p>
                  </div>

                  {npcs.length === 0 && locations.length === 0 && quests.length === 0 && form.linked_items.length === 0 && (
                    <div className="text-center py-6 text-muted-foreground">
                      <Link2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">
                        {lang === 'fr'
                          ? 'Créez des PNJ, lieux ou quêtes pour les lier ici'
                          : 'Create NPCs, locations, or quests to link them here'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ━━ Meta tab ━━ */}
              {activeTab === 'meta' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold">{lang === 'fr' ? 'Acte / Chapitre' : 'Act / Chapter'}</Label>
                      <Input
                        value={form.act}
                        onChange={e => handleChange('act', e.target.value)}
                        placeholder={lang === 'fr' ? 'Ex: Acte 1' : 'Ex: Act 1'}
                        className="h-10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold">Arc</Label>
                      <Input
                        value={form.arc}
                        onChange={e => handleChange('arc', e.target.value)}
                        placeholder={lang === 'fr' ? 'Ex: La trahison du roi' : 'Ex: The king\'s betrayal'}
                        className="h-10"
                      />
                    </div>
                  </div>

                  {/* Switches */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-background/50">
                      <Switch
                        checked={form.is_main_plot}
                        onCheckedChange={v => handleChange('is_main_plot', v)}
                      />
                      <div className="min-w-0">
                        <Label className="text-xs font-semibold flex items-center gap-1.5">
                          <Star className="h-3 w-3 text-primary" />
                          {lang === 'fr' ? 'Intrigue principale' : 'Main Plot'}
                        </Label>
                        <p className="text-[10px] text-muted-foreground">
                          {lang === 'fr' ? 'Marquer comme fil principal' : 'Mark as main thread'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-background/50">
                      <Switch
                        checked={form.is_secret}
                        onCheckedChange={v => handleChange('is_secret', v)}
                      />
                      <div className="min-w-0">
                        <Label className="text-xs font-semibold flex items-center gap-1.5">
                          <Lock className="h-3 w-3 text-slate-500" />
                          Secret
                        </Label>
                        <p className="text-[10px] text-muted-foreground">
                          {lang === 'fr' ? 'Visible MJ uniquement' : 'GM only visibility'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold flex items-center gap-1.5">
                      <Tag className="h-3 w-3" /> Tags
                    </Label>
                    {form.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {form.tags.map((tag, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-muted/60 text-muted-foreground px-2 py-0.5 rounded-lg border border-border/30">
                            #{tag}
                            <X className="h-3 w-3 cursor-pointer opacity-60 hover:opacity-100" onClick={() => removeTag(tag)} />
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        placeholder={lang === 'fr' ? 'Ajouter un tag...' : 'Add tag...'}
                        className="h-9"
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                      />
                      <Button type="button" variant="outline" size="sm" className="h-9 px-3" onClick={addTag}>
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* ── Footer ── */}
          <DialogFooter className="px-5 py-3 border-t border-border/40 gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-xs">
              {lang === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="text-xs font-bold gap-1.5 shadow-md"
              style={{ background: typeConfig.color, color: '#fff' }}
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isEdit
                ? (lang === 'fr' ? 'Enregistrer' : 'Save')
                : (lang === 'fr' ? 'Créer le nœud' : 'Create Node')
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Linked Entity Section ── */
function LinkedSection({ label, items, selected, onToggle, getLabel, color }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
        {items.map(item => {
          const isSelected = selected.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onToggle(item.id)}
              className={cn(
                'text-[11px] font-medium px-2.5 py-1 rounded-lg border transition-all active:scale-95',
                isSelected
                  ? 'border-current/20 shadow-sm'
                  : 'border-border/50 text-muted-foreground hover:text-foreground hover:border-border',
              )}
              style={isSelected ? { background: color + '12', color, borderColor: color + '30' } : {}}
            >
              {getLabel(item)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
